// Shared helpers for the Tasks & Events redesign (Board + Add + Detail).
// The board shows two kinds of items differentiated by a `type` tag:
//   - "task"  : new items in lawyers/{uid}/tasks (priority, assignee, checklist…)
//   - "event" : existing items in lawyers/{uid}/events (hearings auto-created
//               from cases, manual events) — rendered read-mostly.

export const PRIORITY_META = {
  high: { label: "High", dot: "#EF4444", pill: "bg-red-50 text-red-700 ring-red-200" },
  medium: { label: "Medium", dot: "#F59E0B", pill: "bg-amber-50 text-amber-700 ring-amber-200" },
  low: { label: "Low", dot: "#22C55E", pill: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
};

// Kanban columns with their soft header tints (matches the mockup).
export const COLUMNS = [
  { key: "todo", label: "To Do", tint: "bg-amber-50", bar: "text-amber-600" },
  { key: "inprogress", label: "In Progress", tint: "bg-indigo-50", bar: "text-indigo-600" },
  { key: "completed", label: "Completed", tint: "bg-emerald-50", bar: "text-emerald-600" },
  { key: "overdue", label: "Overdue", tint: "bg-red-50", bar: "text-red-600" },
];

export const REMINDER_OPTIONS = [
  { value: "none", label: "No reminder" },
  { value: "1hour", label: "1 hour before" },
  { value: "1day", label: "1 day before" },
  { value: "2days", label: "2 days before" },
  { value: "1week", label: "1 week before" },
  { value: "custom", label: "Custom date & time" },
];

// Human label for a task's reminder; "custom" resolves to its picked date/time.
export const reminderLabel = (reminder, reminderAt) => {
  if (reminder === "custom") {
    const d = toDate(reminderAt);
    return d
      ? d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
      : "Custom date & time";
  }
  return REMINDER_OPTIONS.find((r) => r.value === reminder)?.label || "—";
};

const toDate = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

export const startOfToday = () => {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
};

// Derive the kanban column for either a task or an event.
export const deriveColumn = (item, today = startOfToday()) => {
  const due = toDate(item.dueDate || item.date);
  const past = due ? due < today : false;
  if (item.type === "task") {
    if (item.status === "completed") return "completed";
    if (past) return "overdue";
    return item.status === "inprogress" ? "inprogress" : "todo";
  }
  // event
  if (item.status === "done") return "completed";
  if (past) return "overdue";
  return "todo";
};

export const initialsOf = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-violet-100 text-violet-700",
  "bg-rose-100 text-rose-700",
  "bg-sky-100 text-sky-700",
];
export const avatarColor = (seed) => {
  let h = 0;
  const s = seed || "";
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

export const fmtDueShort = (s) => {
  const d = toDate(s);
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

export const fmtDueLong = (s) => {
  const d = toDate(s);
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
